import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getAllPaymentTransactions } from '../services/membershipService';
import './MembershipPayments.css';

const MembershipPayments = () => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  // Check authentication and authorization
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // Only allow admin and manager roles
    if (!user?.role || !['admin', 'manager'].includes(user.role)) {
      navigate('/');
      return;
    }
  }, [isLoggedIn, user, navigate]);

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;

      const result = await getAllPaymentTransactions({
        ...appliedFilters,
        limit: itemsPerPage,
        offset,
      });

      setTransactions(result.purchases || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      alert('Failed to load payment transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && user) {
      fetchTransactions();
    }
  }, [currentPage, appliedFilters, isLoggedIn, user]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const clearFilters = () => {
    const emptyFilters = {
      status: '',
      startDate: '',
      endDate: '',
      search: '',
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const getStatusBadge = (status, refunded = false) => {
    // If refunded, show Refunded badge prominently
    if (refunded) {
      return <span className="badge bg-warning text-dark">Refunded</span>;
    }

    switch (status) {
      case -1:
        return <span className="badge bg-danger">Failed</span>;
      case 0:
        return <span className="badge bg-secondary">Pending</span>;
      case 1:
        return <span className="badge bg-info">Approved</span>;
      case 3:
        return <span className="badge bg-success">Captured</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount, currency) => {
    return `${currency} ${Number(amount).toFixed(2)}`;
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Export to Excel functionality
  const exportToExcel = () => {
    // Create CSV content
    const headers = [
      'Purchase ID',
      'User Name',
      'Email',
      'Phone',
      'Membership Type',
      'Amount',
      'Currency',
      'Status',
      'Order ID',
      'Transaction ID',
      'Captured',
      'Refunded',
      'Created At',
      'Updated At',
    ];

    const rows = transactions.map((tx) => [
      tx.id,
      tx.userName,
      tx.userEmail,
      tx.userPhone,
      tx.membershipType,
      tx.finalPrice,
      tx.currency,
      tx.paymentStatusText,
      tx.paymobOrderId || 'N/A',
      tx.paymobTransactionId || 'N/A',
      tx.captured ? 'Yes' : 'No',
      tx.refunded ? 'Yes' : 'No',
      formatDate(tx.createdAt),
      formatDate(tx.updatedAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `membership-payments-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && transactions.length === 0) {
    return (
      <>
        <Navbar />
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container my-5">
        <div className="membership-payments-page">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">Payment Transactions</h1>
            <div>
              <button
                className="btn btn-outline-secondary me-2"
                onClick={() => navigate('/membership')}
              >
                Back to Memberships
              </button>
              <button className="btn btn-success" onClick={exportToExcel}>
                <i className="bi bi-download me-2"></i>
                Export to Excel
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Filters</h5>
              <div className="row g-3">
                {/* Search */}
                <div className="col-md-3">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name, email, phone, ID..."
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* Status Filter */}
                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Statuses</option>
                    <option value="-1">Failed</option>
                    <option value="0">Pending</option>
                    <option value="1">Approved</option>
                    <option value="3">Captured</option>
                  </select>
                </div>

                {/* Start Date */}
                <div className="col-md-2">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* End Date */}
                <div className="col-md-2">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>

                {/* Buttons */}
                <div className="col-md-3 d-flex align-items-end">
                  <button className="btn btn-primary me-2" onClick={applyFilters}>
                    Apply Filters
                  </button>
                  <button className="btn btn-outline-secondary" onClick={clearFilters}>
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mb-3">
            <p className="text-muted">
              Showing {transactions.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
              {Math.min(currentPage * itemsPerPage, total)} of {total} transactions
            </p>
          </div>

          {/* Transactions Table */}
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Membership ID</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Order ID</th>
                      <th>Transaction ID</th>
                      <th>Membership Code</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>
                            <div className="small">
                              <strong>{tx.userName}</strong>
                              <br />
                              <span className="text-muted">{tx.userEmail}</span>
                              <br />
                              <span className="text-muted">{tx.userPhone}</span>
                            </div>
                          </td>
                          <td>
                            {tx.membershipId ? (
                              <small className="text-muted">#{tx.membershipId.slice(0, 8)}</small>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>{tx.membershipType}</td>
                          <td>
                            <strong>{formatAmount(tx.finalPrice, tx.currency)}</strong>
                          </td>
                          <td>
                            {getStatusBadge(tx.paymentStatus, tx.refunded)}
                          </td>
                          <td>
                            <code className="small">{tx.paymobOrderId || 'N/A'}</code>
                          </td>
                          <td>
                            <code className="small">{tx.paymobTransactionId || 'N/A'}</code>
                          </td>
                          <td>{tx.membershipCode || 'N/A'}</td>
                          <td className="small">{formatDate(tx.createdAt)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                // Show transaction details in a modal or navigate to detail page
                                alert(JSON.stringify(tx, null, 2));
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                      Previous
                    </button>
                  </li>

                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2)
                    ) {
                      return (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => goToPage(page)}>
                            {page}
                          </button>
                        </li>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return (
                        <li key={page} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    return null;
                  })}

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MembershipPayments;
